import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Mail, Phone, Briefcase, BookOpen, Clock, FileText, Building2, Repeat, Info } from "lucide-react";

// All prices come 1:1 from the official "Preisliste 2025" (Stand 2025).
// Anything not explicitly listed here stays "auf Anfrage / nach Aufwand".

const Section = ({ icon: Icon, title, subtitle, rows, footnote, testId }) => (
  <section className="mb-12" data-testid={testId}>
    <div className="flex items-start gap-4 mb-5">
      <div className="w-11 h-11 rounded-lg bg-[#1A2433] flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-[#DC2626]" />
      </div>
      <div>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2433] tracking-tight leading-tight">{title}</h2>
        {subtitle && <p className="text-sm text-slate-600 mt-1">{subtitle}</p>}
      </div>
    </div>
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-5 py-3 font-semibold">Position</th>
            <th className="text-right px-5 py-3 font-semibold w-32 whitespace-nowrap">Preis CHF</th>
            <th className="text-left px-5 py-3 font-semibold w-40">Einheit</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
              <td className="px-5 py-3 align-top">
                <div className="font-medium text-[#1A2433]">{r.code ? <span className="text-[#991B1B] font-bold mr-2">{r.code}</span> : null}{r.label}</div>
                {r.note && <div className="text-xs text-slate-500 mt-1 leading-snug">{r.note}</div>}
              </td>
              <td className="px-5 py-3 text-right tabular-nums font-semibold text-[#1A2433] align-top whitespace-nowrap">{r.price}</td>
              <td className="px-5 py-3 text-slate-600 align-top whitespace-nowrap">{r.unit}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {footnote && (
      <p className="text-xs text-slate-500 mt-3 leading-relaxed flex gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {footnote}
      </p>
    )}
  </section>
);

const basismodule = [
  { code: "M1", label: "Einfacher Firmensitz als c/o Adresse", price: "150.00", unit: "pro Monat", note: "inkl. Postannahme, lagernd. Beispiel: Ihr Firmenname AG / GmbH c/o MBW Swiss AG, Wildenstrasse 3, 8212 Neuhausen am Rheinfall." },
  { code: "M2", label: "Repräsentativer Firmensitz als postalische Direktadresse", price: "300.00", unit: "pro Monat", note: "inkl. Postannahme, lagernd. Beispiel: Ihr Firmenname AG / GmbH, Wildenstrasse 3, 8212 Neuhausen am Rheinfall." },
];

const post = [
  { code: "P1", label: "Postweiterleitung + Scanservice an E-Mail nach Wunsch, unlimitiert", price: "70.00", unit: "pro Monat" },
  { code: "P2", label: "Postweiterleitung inkl. Versand + Porto", price: "150.00", unit: "pro Monat" },
];

const telekom = [
  { code: "T1", label: "Festnetznummer (041-Vorwahl, auf Wunsch auch andere Kantone)", price: "10.00",  unit: "pro Monat" },
  { code: "T2", label: "Rufweiterleitung auf Festnetz oder Mobil – inkl. Gebühren, Flat", price: "40.00",  unit: "pro Monat" },
  { code: "T3", label: "Domain / Mail-PoP",                                              price: "20.00",  unit: "pro Monat" },
];

const vr = [
  { code: "V1", label: "Verwaltungsrat / Geschäftsführung", price: "3’000.00", unit: "pro Jahr" },
];

const buchhaltung = [
  { code: "B1", label: "Finanzbuchhaltung inkl. 100 Buchungen / Monat", price: "100.00", unit: "pro Monat", note: "Darüber hinaus 0.65 CHF pro Buchungssatz, monatlich abgerechnet." },
  { code: "B2", label: "Lohn- & Gehaltsbuchhaltung (bis 3 Mitarbeitende)",  price: "50.00",  unit: "pro Monat" },
  { code: "B3", label: "Jahresabschluss (GuV, Bilanz, Anhang)",             price: "850.00", unit: "pro Jahr" },
  { code: "B4", label: "Steuererklärung – Juristische Person",              price: "350.00", unit: "pro Jahr" },
];

const stunden = [
  { code: "C1", label: "Aufwand nach Std. – Mitglied der Geschäftsführung", price: "150.00", unit: "pro Stunde" },
  { code: "C2", label: "Aufwand nach Std. – kaufm. Mitarbeitende",          price: "70.00",  unit: "pro Stunde" },
];

const gruendung = [
  { label: "Gründung einer GmbH",            price: "5’000.00",  unit: "Pauschal" },
  { label: "Gründung einer AG",              price: "7’000.00",  unit: "Pauschal" },
  { label: "Gründung einer Holding AG/GmbH", price: "10’000.00", unit: "Pauschal" },
  { label: "Gründung komplexer AG / Holding",price: "15’000.00", unit: "Pauschal" },
];

const mutationen = [
  { label: "Sitzwechsel ohne Statutenänderung",                price: "2’000.00", unit: "Pauschal" },
  { label: "Sitzwechsel & sonstige Mutationen mit Statutenänderung", price: "3’500.00", unit: "Pauschal" },
];

export default function PreislistePage() {
  return (
    <div className="pt-32 pb-24 bg-slate-50 min-h-screen" data-testid="preisliste-page">
      <div className="mbw-container max-w-5xl">
        {/* Header */}
        <div className="mb-12">
          <div className="mbw-overline mb-4">Preisliste 2025</div>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-[#1A2433] leading-[1.02] mb-6">
            Transparente Preise. <span className="text-[#991B1B]">Ohne Überraschungen.</span>
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">
            Unsere offizielle Preisliste – gültig ab 2025. Alle Preise verstehen sich in
            Schweizer Franken (CHF) und <strong>zzgl. MWST</strong>. Möchten Sie Ihren individuellen
            Aufwand abschätzen? Nutzen Sie den{" "}
            <Link to="/rechner" className="text-[#991B1B] underline hover:no-underline font-medium">Kostenrechner</Link>.
          </p>
        </div>

        {/* Einleitung Basismodule */}
        <div className="bg-white border-l-4 border-[#991B1B] rounded-r-xl p-6 mb-10 shadow-sm" data-testid="preisliste-intro">
          <h2 className="font-heading text-xl font-bold text-[#1A2433] mb-2">Basismodule Firmendomizil</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            Sie haben bei uns die Wahl zwischen <strong>zwei Basismodulen</strong> an Firmendomizilen
            (M1 + M2). Beide Module sind steuerlich anerkannt. Unter <em>Serviceleistungen</em> können
            Sie als Baukastensystem genau die Zusatzdienste hinzubuchen, die Sie wirklich brauchen –
            Post, Telekommunikation, VR-Mandat, Buchhaltung etc.
          </p>
        </div>

        <Section icon={MapPin}     title="Basismodule"       rows={basismodule} testId="section-basismodule"
          footnote="Beide Module sind steuerlich anerkannt. Die konkrete Domiziladresse legen wir gemeinsam mit Ihnen im Rahmen der Beratung fest – wir unterhalten Büros in den Kantonen Schaffhausen und Zürich." />

        <h3 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2433] tracking-tight mt-16 mb-2">Serviceleistungen</h3>
        <p className="text-slate-600 mb-8">Baukastensystem – individuell hinzubuchbar.</p>

        <Section icon={Mail}      title="Postdienstleistungen" rows={post}        testId="section-post" />
        <Section icon={Phone}     title="Telekommunikation"    rows={telekom}     testId="section-telekom" />
        <Section icon={Briefcase} title="VR-Mandat / Geschäftsführung" rows={vr}  testId="section-vr" />
        <Section icon={BookOpen}  title="Buchhaltung"          rows={buchhaltung} testId="section-buchhaltung"
          footnote="Komplexere Buchhaltungen (z. B. Konzern, internationale MWST) auf Anfrage." />
        <Section icon={Clock}     title="Abrechnung nach Aufwand" rows={stunden}  testId="section-stunden"
          footnote="Für Tätigkeiten, die nicht pauschal abgedeckt sind. Aufwand wird minutengenau erfasst." />

        <h3 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2433] tracking-tight mt-16 mb-2">Einmalige Dienstleistungen</h3>
        <p className="text-slate-600 mb-8">Firmengründungen und Firmenmutationen.</p>

        <Section icon={Building2} title="Firmengründungen"   rows={gruendung}  testId="section-gruendung"
          footnote="Notariats- und Handelsregistergebühren werden separat in Rechnung gestellt (Selbstkosten)." />
        <Section icon={Repeat}    title="Firmenmutationen"   rows={mutationen} testId="section-mutationen" />

        {/* Hinweis */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-10 flex gap-4" data-testid="preisliste-disclaimer">
          <FileText className="w-6 h-6 text-[#991B1B] flex-shrink-0" />
          <div className="text-sm text-slate-600 leading-relaxed">
            <strong className="text-[#1A2433]">Hinweise:</strong> Alle Preise in CHF, zzgl. gesetzlicher MWST (aktuell 8.1 %).
            Auslagen (Notar, Handelsregister, Beglaubigung, Übersetzung, Porto, Drittspesen) werden
            zu Selbstkosten weiterverrechnet. Diese Preisliste ist gültig bis zur Veröffentlichung
            einer neuen Version. Individuelle Vereinbarungen können davon abweichen und werden
            schriftlich im Mandatsvertrag festgehalten.
          </div>
        </div>

        {/* CTA */}
        <div className="bg-[#1A2433] text-white rounded-2xl p-10 md:p-14 text-center">
          <h3 className="font-heading text-3xl md:text-4xl font-bold mb-4">Bereit für Ihren individuellen Kostenvoranschlag?</h3>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            In vier Schritten zu einer unverbindlichen Schätzung – oder direkt zum persönlichen Erstgespräch.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/rechner" className="btn-primary" data-testid="preisliste-cta-calc">
              Zum Kostenrechner <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/kontakt" className="btn-inverse" data-testid="preisliste-cta-contact">
              Erstgespräch vereinbaren
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
